/**
 * Leads — the owner's own lead management (13 Oct 2026).
 *
 * Owner: "they will not upload enquires they will see inquires which i will
 * upload in excel sheets and they will see those details name and number and
 * they are telecallers they will call those people and upload the response
 * like a lead management system", and of the three telecallers: "all 3 should
 * see same sheet once a person mark it the other two cant work on the same
 * lead". Only the owner uploads and sees every lead.
 *
 * A sheet becomes a list of leads in one shared pool. The first telecaller to
 * take a lead — tapping Call, Take, or writing what happened — owns it from
 * then on; it leaves the other two's screens. Every call is written down with
 * its result, and the result decides where the lead stands.
 */
import { HEAD_ADMIN_DESK_EMAILS, isAsmRole, type EnquiryChannelKey } from './domain.js';

/**
 * Where a lead stands. NEW until somebody calls; CONVERTED, NOT_INTERESTED and
 * WRONG_NUMBER are closed; TRANSFERRED is handed to a field person to convert
 * (19 Oct 2026) — off the calling list, its outcome theirs.
 */
export const LEAD_STATUSES = ['NEW', 'FOLLOW_UP', 'INTERESTED', 'CONVERTED', 'NOT_INTERESTED', 'WRONG_NUMBER', 'TRANSFERRED'] as const;
export type LeadStatusKey = (typeof LEAD_STATUSES)[number];
export const LEAD_STATUS_LABELS: Record<LeadStatusKey, string> = {
  NEW: 'Not called yet',
  FOLLOW_UP: 'Call again',
  INTERESTED: 'Interested',
  CONVERTED: 'Converted',
  NOT_INTERESTED: 'Not interested',
  WRONG_NUMBER: 'Wrong number',
  TRANSFERRED: 'Transferred',
};
/** Nobody calls a closed lead again — unless a call written later reopens it. */
export const LEAD_CLOSED_STATUSES: readonly LeadStatusKey[] = ['CONVERTED', 'NOT_INTERESTED', 'WRONG_NUMBER'];
export const LEAD_OPEN_STATUSES: readonly LeadStatusKey[] = ['NEW', 'FOLLOW_UP', 'INTERESTED'];

/** What a call came to — one tap on the telecaller's screen. */
export const LEAD_CALL_RESULTS = ['NO_ANSWER', 'BUSY', 'SWITCHED_OFF', 'CALL_LATER', 'INTERESTED', 'CONVERTED', 'NOT_INTERESTED', 'WRONG_NUMBER'] as const;
export type LeadCallResultKey = (typeof LEAD_CALL_RESULTS)[number];
export const LEAD_CALL_RESULT_LABELS: Record<LeadCallResultKey, string> = {
  NO_ANSWER: 'Did not pick up',
  BUSY: 'Busy',
  SWITCHED_OFF: 'Switched off',
  CALL_LATER: 'Call later',
  INTERESTED: 'Interested',
  CONVERTED: 'Converted',
  NOT_INTERESTED: 'Not interested',
  WRONG_NUMBER: 'Wrong number',
};
/** The status a call's result leaves the lead in. */
export const LEAD_STATUS_AFTER: Record<LeadCallResultKey, LeadStatusKey> = {
  NO_ANSWER: 'FOLLOW_UP',
  BUSY: 'FOLLOW_UP',
  SWITCHED_OFF: 'FOLLOW_UP',
  CALL_LATER: 'FOLLOW_UP',
  INTERESTED: 'INTERESTED',
  CONVERTED: 'CONVERTED',
  NOT_INTERESTED: 'NOT_INTERESTED',
  WRONG_NUMBER: 'WRONG_NUMBER',
};
/** Results that mean try again: the next call is tomorrow unless a day is given. */
export const LEAD_RETRY_RESULTS: readonly LeadCallResultKey[] = ['NO_ANSWER', 'BUSY', 'SWITCHED_OFF'];
/** "Call later" says when. */
export const leadResultNeedsDate = (result: LeadCallResultKey) => result === 'CALL_LATER';

export const LEAD_NOTE_MAX = 1000;
/** The most rows one sheet may bring in. */
export const LEAD_SHEET_MAX_ROWS = 20_000;

/** Who uploads sheets, sees every lead and moves them: the owner's head-admin desks alone. */
export function managesLeads(user: { email: string } | null | undefined): boolean {
  return Boolean(user) && HEAD_ADMIN_DESK_EMAILS.includes(user!.email.trim().toLowerCase());
}

/** Who calls them: the telecalling team. */
export function callsLeads(roleKey: string | null | undefined): boolean {
  return roleKey === 'TELECALLER';
}

/**
 * Handing a lead over for conversion (owner, 19 Oct 2026: "add a option to
 * transfer dealer enquiry to manish or goswami the telecaller can also
 * trasnfer the lead for conversion"). The lead goes to a person switched on
 * as a lead taker in Users — today Manish and Goswami — and lands in their
 * enquiry book as a dealer enquiry carrying its calls. The telecaller keeps
 * the credit and sees how it ends; only the taker writes on it from then on.
 */
export const LEAD_TRANSFER_NOTE_MAX = 500;

/** Who may be switched on as a lead taker: someone who keeps an enquiry book — an ASM or a sales head. */
export function mayTakeLeads(roleKey: string | null | undefined): boolean {
  return isAsmRole(roleKey);
}

/** What became of a handed-over lead, as its taker's enquiry says. */
export type LeadTransferOutcome = 'OPEN' | 'WON' | 'LOST';
export const LEAD_TRANSFER_OUTCOME_LABELS: Record<LeadTransferOutcome, string> = {
  OPEN: 'With them',
  WON: 'Converted',
  LOST: 'Lost',
};

/** A handed-over lead, as its row shows it. */
export interface LeadTransfer {
  to: { id: string; name: string } | null;
  by: { id: string; name: string } | null;
  at: string;
  outcome: LeadTransferOutcome;
  /** When the taker last wrote on it themselves; null until they do. */
  lastUpdateAt: string | null;
  settledAt: string | null;
  /** The enquiry it became — the owner's link to it; null for a telecaller. */
  enquiryId: string | null;
}

/** Someone a lead can be handed to. */
export interface LeadTaker {
  id: string;
  name: string;
  role: string;
  /** Leads handed to them still open there. */
  open: number;
}

/**
 * A heading on each sheet (owner, 2 Nov 2026: "i have 2-3 types of leads one
 * for dealer enquiry one for other so give me a option to give heading to the
 * lead sheet while uploading"). It says what kind of leads the sheet holds —
 * every lead from it carries it, the lists filter on it, and a lead handed
 * over lands in the taker's book as that kind of enquiry.
 */
export const LEAD_HEADING_MAX = 60;
/** Offered on the upload until the owner has headings of their own. */
export const LEAD_HEADING_SUGGESTIONS: readonly string[] = ['Dealer enquiry', 'Customer enquiry'];

/**
 * What a lead becomes in the taker's enquiry book when it is handed over, read
 * off its sheet's heading. A dealer unless the heading plainly says a customer,
 * a mitr or an outlet — the hand-over was built for dealer enquiries
 * (19 Oct 2026), so that stays the default for a sheet with no heading.
 */
export function channelForHeading(heading: string | null | undefined): EnquiryChannelKey {
  const h = (heading ?? '').toLowerCase();
  // A mitr first: the business calls one a sub dealer (BUSINESS-RULES §2), and "dealer" would match that too.
  if (/sub[\s-]?dealer|उप\s?डीलर|\bmit(h)?ra?s?\b|मित्र/.test(h)) return 'MITRA';
  if (/dealer|डीलर/.test(h)) return 'DEALER';
  if (/customer|retail|grahak|buyer|ग्राहक/.test(h)) return 'CUSTOMER';
  if (/outlet|showroom|counter/.test(h)) return 'OUTLET';
  return 'DEALER';
}

/**
 * Splitting a sheet evenly between telecallers (owner, 2 Nov 2026: "a option
 * to evenly split the leads between people"). The leads are dealt out in the
 * sheet's order, one each in turn — so everybody gets the top of the sheet and
 * the bottom alike — and nobody gets more than one more than another. When the
 * leads do not divide exactly, the spare ones go to whoever holds the fewest
 * open leads already. This one rule is the server's split and the upload
 * screen's preview of it.
 */
export const LEAD_SPLIT_MAX_PEOPLE = 50;

export interface LeadSplitPerson {
  id: string;
  name: string;
  /** Open leads they hold now. */
  open: number;
}

/** The order the leads are dealt in: fewest open leads first, then by name. */
export function splitOrder<T extends LeadSplitPerson>(people: readonly T[]): T[] {
  return [...people].sort((a, b) => a.open - b.open || a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

/** How many of `total` leads each person gets, in dealing order. */
export function evenShares<T extends LeadSplitPerson>(total: number, people: readonly T[]): Array<T & { share: number }> {
  const order = splitOrder(people);
  const n = order.length;
  if (n === 0) return [];
  const base = Math.floor(Math.max(0, total) / n);
  const spare = Math.max(0, total) - base * n;
  return order.map((p, i) => ({ ...p, share: base + (i < spare ? 1 : 0) }));
}

/** Who gets the lead at position `index` of the sheet, in dealing order. */
export const dealtTo = <T>(order: readonly T[], index: number): T => order[index % order.length]!;
